import { Inject, Injectable, Optional } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { catchError, delay, finalize, map } from 'rxjs/operators';

import { WebActivityReportsPort } from '../../../app/ports/reports/web-activity-reports.port';

import { AppEventsService } from '../../events/products-events.service';
import { CoreUIToastDirection, CoreUIToastV2Service } from '../../services/toast.service';
import { ReportSdk } from 'lib-klab-sdks';

import { EnvMicroReport } from '../../config/env.micro.report';
import { REPORT_ENV } from '../../config/micros-env.tokens';
import { WebActivityReportRequest } from '../../../domain/models/reports/request/web-activity-report.request';
import { WebActivityReportResponse } from '../../../domain/models/reports/web-activity-report.model';
import { mapSdkWebActivityListResponseToDomain, mapSdkWebsTopWithPercentageResponseToDomain } from '../../mappers/reports/web-activity-reports.mapper';
import { GroupedWebActivityReportRequest } from '../../../domain/models/reports/request/grouped-web-activity-report.request';
import { WebsTopWithPercentageResponse } from '../../../domain/models/reports/webs-top-percentage.model';
import { webActivityReportsSdkMock } from '../../mocks/report/webs-activity-reports.mock';

type WebActivityReportsMockAction = 'getWebActivitiesList' | 'getWebsTopWithPercentage';

@Injectable({ providedIn: 'root' })
export class WebActivityReportsPortAdapter implements WebActivityReportsPort {
    constructor(
        private webActivityReportsService: ReportSdk.WebActivityReportsService,
        private appEvents: AppEventsService,
        @Optional() private toast: CoreUIToastV2Service,
        @Inject(REPORT_ENV) private env: EnvMicroReport,
    ) { }

    getWebActivitiesList(payload: WebActivityReportRequest): Observable<WebActivityReportResponse> {
        this.appEvents.loaderVisiblePublish(true, 'report-activity-web');

        const sdkPayload: ReportSdk.ActivityRequest = {
            employeeId: payload.employeeId,
            dateRange: {
                startDate: payload.dateRange.startDate,
                endDate: payload.dateRange.endDate,
            },
            ...(payload.page !== undefined ? { page: payload.page } : {}),
            ...(payload.size !== undefined ? { size: payload.size } : {}),
        };

        const source$: Observable<ReportSdk.WebActivityListResponse> = this.shouldMock('getWebActivitiesList')
            ? of(
                webActivityReportsSdkMock.getWebActivitiesList(
                    sdkPayload.employeeId,
                    sdkPayload.dateRange,
                    sdkPayload.page ?? 0,
                    sdkPayload.size ?? 20,
                ),
            ).pipe(delay(360))
            : this.webActivityReportsService.getWebActivitiesList(sdkPayload);

        return source$.pipe(
            map((resp) => mapSdkWebActivityListResponseToDomain(resp as any)),
            catchError((err) => {
                this.showErrorToast(err);
                return throwError(() => err);
            }),
            finalize(() => this.appEvents.loaderVisiblePublish(false, 'report-activity-web')),
        );
    }

    getWebsTopWithPercentage(
        payload: GroupedWebActivityReportRequest,
    ): Observable<WebsTopWithPercentageResponse> {
        this.appEvents.loaderVisiblePublish(true, 'report-activity-web-top');

        const sdkPayload: ReportSdk.GroupedActivityRequest = {
            employeeId: payload.employeeId,
            dateRange: {
                startDate: payload.dateRange.startDate,
                endDate: payload.dateRange.endDate,
            },
        };

        const source$: Observable<ReportSdk.GroupedWebsResponse> = this.shouldMock('getWebsTopWithPercentage')
            ? of(
                webActivityReportsSdkMock.getWebsTopWithPercentage(
                    sdkPayload.employeeId,
                    sdkPayload.dateRange,
                ),
            ).pipe(delay(360))
            : this.webActivityReportsService.getWebsTopWithPercentage(sdkPayload);

        return source$.pipe(
            map((resp) => mapSdkWebsTopWithPercentageResponseToDomain(resp as any)),
            catchError((err) => {
                this.showErrorToast(err);
                return throwError(() => err);
            }),
            finalize(() => this.appEvents.loaderVisiblePublish(false, 'report-activity-web-top')),
        );
    }

    private shouldMock(action: WebActivityReportsMockAction): boolean {
        return Boolean(this.env?.useMocks || this.env?.mock?.webActivityReports?.[action]);
    }

    private showErrorToast(err: any) {
        if (!this.toast) return;

        const status = err?.status;
        const mapMsg: Record<number, string> = {
            400: 'Solicitud inválida',
            401: 'No autorizado',
            404: 'No encontrado',
            429: 'Demasiadas solicitudes',
            500: 'Error interno del servidor',
        };

        this.toast.show({
            data: {
                title: 'Error',
                msg: mapMsg[status] || 'Error inesperado',
                direction: CoreUIToastDirection.topCenter,
                autoClose: true,
            },
        });
    }
}
